import React, { useState } from 'react'
import { Box, ChevronDownIcon, ChevronUpIcon, Flex, IconButton, Text, useTooltip } from 'uikit'
import { useWeb3React } from '@web3-react/core'
import styled from 'styled-components'
import useLocalDispatch from 'contexts/LocalRedux/useLocalDispatch'
import { Bet, PredictionStatus } from 'state/types'
import { REWARD_RATE } from 'state/predictions/config'
import { useGetCurrentEpoch, useGetIsClaimable, useGetPredictionsStatus } from 'state/predictions/hooks'
import { fetchLedgerData, markAsCollected } from 'state/predictions'
import { getRoundResult, Result } from 'state/predictions/helpers'
import { useTranslation } from 'react-i18next'
import { formatMjt, getNetPayout } from './helpers'
import { CollectWinningsNormalButton as CollectWinningsButton } from '../CollectWinningsButton'
import ReclaimPositionButton from '../ReclaimPositionButton'
import BetDetails from './BetDetails'

interface BetProps {
  bet: Bet
}

const StyledBet = styled(Flex).attrs({ alignItems: 'center', p: '16px' })<{ isOpen: boolean }>`
  border: 1px solid rgba(115, 126, 141, 0.16);
  border-radius: ${({ isOpen }) => (isOpen ? '16px 16px 0 0' : '16px')};
  height: 64px;
  margin: 0 24px 12px 24px;
  cursor: pointer;
`

const YourResult = styled(Box)`
  flex: 1;
`

const IconBg = styled(Flex)<{ status: string }>`
  background: ${({ status }) => (status === 'live' ? '#14D89D0D' : status === 'soon' ? '#39D9FF0D' : '#14D89D0D')};
  height: 24px;
  border-radius: 12px;
  padding: 5.5px;
  justify-content: center;
  align-items: center;
  width: 63px;
`

const ImgIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 4px;
`

const StyledClaimBtn = styled(CollectWinningsButton)`
  border: 1px solid #14d89d;
  background: white;
  color: #14d89d;
  height: 32px;
`

const Imgs = {
  live: require('assets/images/prediction/live.svg').default,
  later: require('assets/images/prediction/later.svg').default,
}

const HistoricalBet: React.FC<BetProps> = ({ bet }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { amount, round } = bet
  const { t } = useTranslation()
  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    <>
      <Text bold mb="4px">
        {t('PREDICTION_28')}
      </Text>
      <Text>{t('PREDICTION_29')}</Text>
    </>,
    { placement: 'top' }
  )

  const currentEpoch = useGetCurrentEpoch()
  const status = useGetPredictionsStatus()
  const canClaim = useGetIsClaimable(bet.round.epoch)
  const dispatch = useLocalDispatch()
  const { account } = useWeb3React()

  const toggleOpen = () => setIsOpen(!isOpen)

  const getRoundColor = (result) => {
    switch (result) {
      case Result.WIN:
        return 'success'
      case Result.LOSE:
        return '#F9479C'
      case Result.CANCELED:
        return 'textRemark'
      case Result.HOUSE:
        return 'textRemark'
      default:
        return 'text'
    }
  }

  const getRoundPrefix = (result) => {
    if (result === Result.LOSE) {
      return '-'
    }

    if (result === Result.WIN) {
      return '+'
    }

    return ''
  }

  const roundResult = getRoundResult(bet, currentEpoch)
  const resultTextColor = getRoundColor(roundResult)
  const resultTextPrefix = getRoundPrefix(roundResult)
  const isOpenRound = round.epoch === currentEpoch
  const isLiveRound = status === PredictionStatus.LIVE && round.epoch === currentEpoch - 1

  // Winners get the payout, otherwise the claim what they put it if it was canceled
  const payout = roundResult === Result.WIN ? getNetPayout(bet, REWARD_RATE) : amount

  const renderBetLabel = () => {
    if (isOpenRound) {
      return (
        <IconBg status="soon">
          <ImgIcon src={Imgs.later} />
          <Text color="#39D9FF" fontSize="14px">
            {t('PREDICTION_42')}
          </Text>
        </IconBg>
      )
    }

    if (isLiveRound) {
      return (
        <IconBg status="live">
          <ImgIcon src={Imgs.live} />
          <Text color="primary" fontSize="14px">
            {t('PREDICTION_59')}
          </Text>
        </IconBg>
      )
    }

    return (
      <>
        <Text fontSize="12px" color="textSubtle">
          {t('PREDICTION_78')}
        </Text>
        <Text bold color={resultTextColor} lineHeight={1} mt="2px">
          {roundResult === Result.CANCELED ? (
            t('PREDICTION_41')
          ) : roundResult === Result.HOUSE ? (
            <>
              {tooltipVisible && tooltip}
              <Flex alignItems="center" ref={targetRef}>
                {t('PREDICTION_27')}
                {/* <InfoIcon width="16px" ml="4px" color="secondary" /> */}
              </Flex>
            </>
          ) : (
            `${resultTextPrefix}${formatMjt(Math.abs(payout))}`
          )}
        </Text>
      </>
    )
  }

  const handleSuccess = async () => {
    // We have to mark the bet as claimed immediately because it does not update fast enough
    dispatch(markAsCollected({ [bet.round.epoch]: true }))
    dispatch(fetchLedgerData({ account, epochs: [bet.round.epoch] }))
  }

  return (
    <>
      <StyledBet onClick={toggleOpen} role="button" isOpen={isOpen}>
        <Box width="48px">
          <Text textAlign="center">
            <Text fontSize="12px" color="textSubtle">
              {t('PREDICTION_79')}
            </Text>
            <Text bold lineHeight={1} mt="2px">
              {round.epoch.toLocaleString()}
            </Text>
          </Text>
        </Box>
        <YourResult px="24px">{renderBetLabel()}</YourResult>
        {roundResult === Result.WIN && canClaim && (
          <StyledClaimBtn hasClaimed={!canClaim} onSuccess={handleSuccess} scale="sm" mr="8px">
            {t('PREDICTION_80')}
          </StyledClaimBtn>
        )}
        {roundResult === Result.CANCELED && canClaim && (
          <ReclaimPositionButton epoch={bet.round.epoch} scale="sm" mr="8px">
            {t('PREDICTION_81')}
          </ReclaimPositionButton>
        )}
        {!isOpenRound && !isLiveRound && (
          <IconButton variant="text" scale="sm">
            {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </IconButton>
        )}
      </StyledBet>
      {isOpen && <BetDetails bet={bet} result={getRoundResult(bet, currentEpoch)} />}
    </>
  )
}

export default HistoricalBet
